import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Tooltip from "@material-ui/core/Tooltip";
import CircularProgress from "@material-ui/core/CircularProgress";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import TableChartIcon from "@material-ui/icons/TableChart";
import MapIcon from "@material-ui/icons/Map";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import VpnKeyIcon from "@material-ui/icons/VpnKey";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import SearchIcon from "@material-ui/icons/Search";
import GetAppIcon from "@material-ui/icons/GetApp";
import CloseIcon from "@material-ui/icons/Close";
import MenuIcon from "@material-ui/icons/Menu";
import {
  GoogleMap,
  LoadScript,
  MarkerClusterer,
  Marker,
} from "@react-google-maps/api";
import { Alert, AlertTitle } from "@material-ui/lab";
import moment from "moment";
import _ from "lodash";

import Button from "components/CustomButtons/Button.js";
import { resetCustomerChatSession } from "components/CustomerChatWidget/CustomerChatWidget";
import { getWithToken, postWithToken } from "../../utility";
import styles from "assets/jss/material-kit-react/views/ordersPage.js";

const useStyles = makeStyles(styles);
const statusMap = {
  0: { status: "In Transit", color: "#2563eb" },
  1: { status: "In Transit", color: "#2563eb" },
  2: { status: "Delivered", color: "#14804a" },
  3: { status: "Cancelled", color: "#b42318" },
};
const passwordMeetsPolicy = (password = "") =>
  password.length >= 8 && /[A-Z]/.test(password) && /\d/.test(password);

export default function OrdersPage(props) {
  const classes = useStyles();
  const {
    orders,
    setOrders,
    shipDate,
    setShipDate,
    invoice,
    setInvoice,
  } = props;
  const [activeView, setActiveView] = useState("table");
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [mode, setMode] = useState("latest");
  const [windowLabel, setWindowLabel] = useState("");
  const [isLoadingOrder, setIsLoadingOrder] = useState(false);
  const [filesLoading, setFilesLoading] = useState(false);
  const [fileGroups, setFileGroups] = useState([]);
  const [apiRequestLoading, setApiRequestLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [profileForm, setProfileForm] = useState({ email: "", phone: "" });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [notice, setNotice] = useState("");
  const [isEmailPromptDismissed, setIsEmailPromptDismissed] = useState(false);
  const [isMapLoadError, setIsMapLoadError] = useState(false);
  const [sortStatus, setSortStatus] = useState({
    invoice: false,
    shipdate: false,
    liquorStoreCity: false,
    liquorStoreState: false,
    status: false,
  });
  const [trackingDialog, setTrackingDialog] = useState({
    open: false,
    row: null,
    loading: false,
    items: [],
    error: "",
  });

  const mappableOrders = useMemo(
    () =>
      orders
        .map((order) => ({
          ...order,
          lat: parseFloat(order.lat),
          lng: parseFloat(order.lng),
        }))
        .filter(
          (order) => Number.isFinite(order.lat) && Number.isFinite(order.lng)
        ),
    [orders]
  );

  const unverifiedEmail =
    profile &&
    !isEmailPromptDismissed &&
    (!profile.email || !profile.email.isVerified);
  const selectedShipDate = shipDate
    ? moment(shipDate).format("YYYY-MM-DD")
    : "";
  const apiAccessReady = Boolean(
    profile?.email?.isVerified && profile?.email?.phone
  );
  const apiDocsUrl = 'https://custfdl.longwind.us/api/customer/v1/docs';

  useEffect(() => {
    loadDashboard();
    loadProfile();
    loadFilesOverview();
  }, []);

  useEffect(() => {
    const relabelChat = () => {
      document.querySelectorAll(".chat-plugin-launcher").forEach((node) => {
        if (node.textContent && node.textContent.trim() !== "Tech Support") {
          node.textContent = "Tech Support";
        }
        if (node.getAttribute("aria-label") !== "Tech Support") {
          node.setAttribute("aria-label", "Tech Support");
        }
      });
    };
    relabelChat();
    const observer = new MutationObserver(relabelChat);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const showNotice = (message) => {
    setNotice(message);
    if (message) setTimeout(() => setNotice(""), 5000);
  };

  const loadDashboard = () => {
    setIsLoadingOrder(true);
    getWithToken("/dashboard")
      .then((res) => res.json())
      .then((data) => {
        setOrders(Array.isArray(data.orders) ? data.orders : []);
        setMode(data.mode || "latest");
        setWindowLabel(
          data.windowStart && data.windowEnd
            ? `${data.windowStart} to ${data.windowEnd}`
            : "Last 30 days"
        );
        setShipDate(data.shipDate ? moment(data.shipDate) : null);
        setIsLoadingOrder(false);
      })
      .catch((error) => {
        console.log("Error loading dashboard: ", error);
        setOrders([]);
        setIsLoadingOrder(false);
        showNotice("Unable to load dashboard data.");
      });
  };

  const loadFilesOverview = () => {
    setFilesLoading(true);
    getWithToken("/files/overview")
      .then((res) => res.json())
      .then((data) => {
        setFileGroups(Array.isArray(data.groups) ? data.groups : []);
        setFilesLoading(false);
      })
      .catch((error) => {
        console.log("Error loading files: ", error);
        setFileGroups([]);
        setFilesLoading(false);
      });
  };

  const loadProfile = () => {
    getWithToken("/profile")
      .then((res) => res.json())
      .then((data) => {
        const loginName = (data.user?.username || "").toLowerCase();
        const savedPhone = data.email?.phone || "";
        setProfile(data);
        setProfileForm({
          email: data.email?.email || "",
          phone: savedPhone.toLowerCase() === loginName ? "" : savedPhone,
        });
      })
      .catch((error) => console.log("Error loading profile: ", error));
  };

  const filterByDate = () => {
    if (!selectedShipDate) return;
    setIsLoadingOrder(true);
    setMode("date");
    setInvoice("");
    getWithToken(`/orders?shipdate=${selectedShipDate}&invoice=`)
      .then((res) => res.json())
      .then((data) => {
        setOrders(Array.isArray(data) ? data : []);
        setIsLoadingOrder(false);
      })
      .catch((error) => {
        console.log("Error loading orders: ", error);
        setIsLoadingOrder(false);
        showNotice("Unable to load orders for this date.");
      });
  };

  const searchInvoice = () => {
    const value = (invoice || "").trim();
    if (!value) return;
    setIsLoadingOrder(true);
    setMode("invoice");
    getWithToken(`/orders/search?invoice=${encodeURIComponent(value)}`)
      .then((res) => res.json())
      .then((data) => {
        setOrders(Array.isArray(data.orders) ? data.orders : []);
        setIsLoadingOrder(false);
      })
      .catch((error) => {
        console.log("Error searching invoice: ", error);
        setIsLoadingOrder(false);
        showNotice("Unable to search invoice.");
      });
  };

  const openTrackingDialog = (row) => async () => {
    setTrackingDialog({ open: true, row, loading: true, items: [], error: "" });

    try {
      const res = await postWithToken("/tracking", {
        body: JSON.stringify({
          shipDate: row.shipdate,
          stopNumber: row.stopNumber,
          routeId: row.routeId,
        }),
      });

      if (!res.ok) throw new Error("Tracking unavailable");

      const truckLoadingTime = formatTrackingTime(row.truckLoadingTime);
      const items = (await res.json()).map((item) =>
        item.title === "Truck" && truckLoadingTime
          ? { ...item, message: `Loaded on truck: ${truckLoadingTime}` }
          : item
      );
      setTrackingDialog({ open: true, row, loading: false, items, error: "" });
    } catch (error) {
      console.log("Error loading tracking: ", error);
      setTrackingDialog({
        open: true,
        row,
        loading: false,
        items: [],
        error: "Unable to load delivery status for this invoice.",
      });
    }
  };

  const formatTrackingTime = (value) => {
    if (!value) return "";
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format("MM/DD/YYYY hh:mm a") : value;
  };

  const closeTrackingDialog = () => {
    setTrackingDialog({
      open: false,
      row: null,
      loading: false,
      items: [],
      error: "",
    });
  };

  const getTrackingAddress = (address) => {
    if (!address) return "";
    return [
      address.address1,
      address.address2,
      address.city,
      address.state,
      address.zipcode,
    ]
      .filter(Boolean)
      .join(" ");
  };

  const logout = async () => {
    try {
      await resetCustomerChatSession();
      window.location.href = "/login";
    } catch {
      window.alert('Logout could not be completed. Please retry.');
    }
  };

  const sortOnField = (fieldName) => () => {
    const next = sortStatus[fieldName]
      ? _.reverse([...orders])
      : _.sortBy(orders, [fieldName]);
    setOrders(next);
    setSortStatus({
      invoice: false,
      shipdate: false,
      liquorStoreCity: false,
      liquorStoreState: false,
      status: false,
      [fieldName]: true,
    });
  };

  const openFile = (file) => async () => {
    const downloadWindow = window.open("", "_blank");
    try {
      const rawResponse = await postWithToken("/invoice/bol", {
        body: JSON.stringify({ bolPath: file.path }),
      });
      const response = await rawResponse.json();
      if (response.bolPath) {
        if (downloadWindow) downloadWindow.location = response.bolPath;
        else window.open(response.bolPath, "_blank");
      } else {
        if (downloadWindow) downloadWindow.close();
        showNotice("Unable to create a download link for this file.");
      }
    } catch (e) {
      if (downloadWindow) downloadWindow.close();
      showNotice("Unable to open this file.");
    }
  };

  const getSignatureCell = (row) => {
    if (!row.bolPath) return <div />;
    return (
      <a onClick={openFile({ path: row.bolPath })} className={classes.textLink}>
        View
      </a>
    );
  };

  const exportExcel = () => {
    const headers = [
      "Ship Date",
      "Invoice",
      "Route",
      "Store",
      "City",
      "State",
      "Status",
      "Comments",
      "Signature",
    ];
    const rows = orders.map((row) => [
      row.shipdate,
      row.invoice,
      row.routeId,
      row.liquorStoreName,
      row.liquorStoreCity,
      row.liquorStoreState,
      (statusMap[row.status] || statusMap["0"]).status,
      row.comments || row.ptdesc || "",
      row.bolPath ? "Available" : "",
    ]);
    const html = `<table><thead><tr>${headers
      .map((h) => `<th>${h}</th>`)
      .join("")}</tr></thead><tbody>${rows
      .map(
        (row) =>
          `<tr>${row
            .map(
              (value) =>
                `<td>${String(value || "").replace(
                  /[<>&]/g,
                  (ch) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[ch])
                )}</td>`
            )
            .join("")}</tr>`
      )
      .join("")}</tbody></table>`;
    const blob = new Blob([html], { type: "application/vnd.ms-excel" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `orders-${mode}-${moment().format("YYYYMMDD-HHmm")}.xls`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const saveProfile = () => {
    postWithToken("/profile", { body: JSON.stringify(profileForm) })
      .then((res) => res.json())
      .then((data) => {
        setProfile((prev) => ({ ...prev, email: data.email }));
        showNotice(
          data.emailWarning ||
            (data.emailSent ? "Verification email sent." : "Profile saved.")
        );
      })
      .catch(() => showNotice("Unable to save profile."));
  };

  const requestApiKey = () => {
    if (!apiAccessReady || apiRequestLoading) return;
    setApiRequestLoading(true);
    postWithToken("/profile/api-key-request", { body: JSON.stringify({}) })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        showNotice(
          ok && data.isSuccessful
            ? data.message || "API key request sent."
            : data.message || "Unable to request API key."
        );
      })
      .catch(() => showNotice("Unable to request API key."))
      .finally(() => setApiRequestLoading(false));
  };

  const changePassword = () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword)
      return showNotice("New passwords do not match.");
    if (!passwordMeetsPolicy(passwordForm.newPassword))
      return showNotice(
        "New password needs 8 characters, 1 uppercase letter, and 1 number."
      );
    postWithToken("/profile/password", {
      body: JSON.stringify({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        showNotice(
          data.isSuccessful
            ? "Password changed."
            : "Current password is incorrect."
        );
        if (data.isSuccessful)
          setPasswordForm({
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
          });
      })
      .catch(() => showNotice("Unable to change password."));
  };

  const summary = [
    { label: "Orders", value: orders.length },
    { label: "Mappable", value: mappableOrders.length },
    { label: "File dates", value: fileGroups.length },
    { label: "Window", value: windowLabel || "Last 30 days" },
  ];

  const ordersTable = () => (
    <TableContainer component={Paper} className={classes.tableWrap}>
      <Table className={classes.table} aria-label="orders table">
        <TableHead>
          <TableRow>
            <TableCell onClick={sortOnField("shipdate")}>Ship Date</TableCell>
            <TableCell onClick={sortOnField("invoice")}>Invoice</TableCell>
            <TableCell>Route</TableCell>
            <TableCell>Store</TableCell>
            <TableCell onClick={sortOnField("liquorStoreCity")}>City</TableCell>
            <TableCell onClick={sortOnField("liquorStoreState")}>
              State
            </TableCell>
            <TableCell onClick={sortOnField("status")}>Status</TableCell>
            <TableCell>Comments</TableCell>
            <TableCell>Signature</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {orders.map((row) => {
            const status = statusMap[row.status] || statusMap["0"];
            return (
              <TableRow
                key={row._id || `${row.shipdate}-${row.invoice}-${row.routeId}`}
              >
                <TableCell>{row.shipdate}</TableCell>
                <TableCell>
                  <button
                    type="button"
                    className={classes.invoiceButton}
                    onClick={openTrackingDialog(row)}
                  >
                    {row.invoice}
                  </button>
                </TableCell>
                <TableCell>{row.routeId}</TableCell>
                <TableCell>{row.liquorStoreName}</TableCell>
                <TableCell>{row.liquorStoreCity}</TableCell>
                <TableCell>{row.liquorStoreState}</TableCell>
                <TableCell>
                  <span
                    className={classes.statusPill}
                    style={{ color: status.color }}
                  >
                    {status.status}
                  </span>
                </TableCell>
                <TableCell>
                  {row.comments ? (
                    <Tooltip title={row.comments}>
                      <span className={classes.textLink}>Notes</span>
                    </Tooltip>
                  ) : (
                    row.ptdesc
                  )}
                </TableCell>
                <TableCell>{getSignatureCell(row)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {!orders.length && (
        <div className={classes.emptyState}>
          No orders found for the current view.
        </div>
      )}
    </TableContainer>
  );

  const ordersMap = () => {
    if (!orders.length)
      return (
        <Alert severity="info">
          <AlertTitle>No data</AlertTitle>The map is available when the current
          table has data.
        </Alert>
      );
    if (orders.length && !mappableOrders.length)
      return (
        <Alert severity="warning">
          <AlertTitle>No mappable orders</AlertTitle>The current orders do not
          include valid latitude and longitude values.
        </Alert>
      );
    return (
      <div className={classes.mapWrapper}>
        {isMapLoadError && (
          <Alert severity="warning">
            <AlertTitle>Map unavailable</AlertTitle>Google Maps could not load.
          </Alert>
        )}
        <LoadScript
          googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAP_API_KEY}
          onError={() => setIsMapLoadError(true)}
        >
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            center={{ lat: 40.0583, lng: -74.4057 }}
            zoom={10}
            onLoad={(map) => {
              const bounds = new window.google.maps.LatLngBounds();
              mappableOrders.forEach((order) =>
                bounds.extend(
                  new window.google.maps.LatLng(order.lat, order.lng)
                )
              );
              map.fitBounds(bounds);
            }}
          >
            <MarkerClusterer
              options={{
                imagePath: `${process.env.REACT_APP_SERVER_URL}/markerclusterer/m`,
              }}
            >
              {(clusterer) =>
                mappableOrders.map(({ lat, lng, invoice }, i) => (
                  <Marker
                    key={`${invoice}-${i}`}
                    position={{ lat, lng }}
                    icon={`${process.env.REACT_APP_SERVER_URL}/bottle-10-32.ico`}
                    clusterer={clusterer}
                  />
                ))
              }
            </MarkerClusterer>
          </GoogleMap>
        </LoadScript>
      </div>
    );
  };

  const filesOverview = () => {
    if (filesLoading) return <CircularProgress />;
    if (!fileGroups.length)
      return (
        <Alert severity="info">
          <AlertTitle>No files found</AlertTitle>No PDFs are available in the
          last 30 days.
        </Alert>
      );
    return (
      <div className={classes.fileGrid}>
        {fileGroups.map((group) => (
          <section key={group.shipDate} className={classes.fileGroup}>
            <h3>{group.shipDate}</h3>
            {group.files.map((file) => (
              <button
                type="button"
                key={file.path}
                className={classes.fileButton}
                onClick={openFile(file)}
              >
                <InsertDriveFileIcon />
                {file.id}
              </button>
            ))}
          </section>
        ))}
      </div>
    );
  };

  const profilePanel = () => (
    <div className={classes.profileGrid}>
      <section className={classes.profileSection}>
        <h3>Profile</h3>
        <dl className={classes.readOnlyList}>
          <dt>Login name</dt>
          <dd>{profile?.user?.username || "-"}</dd>
        </dl>
        <TextField
          label="Email"
          fullWidth
          margin="dense"
          variant="outlined"
          value={profileForm.email}
          onChange={(e) =>
            setProfileForm({ ...profileForm, email: e.target.value })
          }
        />
        <TextField
          label="Phone"
          fullWidth
          margin="dense"
          variant="outlined"
          value={profileForm.phone}
          onChange={(e) =>
            setProfileForm({ ...profileForm, phone: e.target.value })
          }
        />
        <Button
          color="primary"
          className={classes.actionButton}
          onClick={saveProfile}
        >
          Save profile
        </Button>
        <div className={classes.verifyText}>
          {profile?.email?.isVerified
            ? "Email verified"
            : "Email is not verified"}
        </div>
        <div className={classes.supportBox}>
          <strong>Tech Support</strong>
          <span>
            For portal help, email <strong>support@longwind.us</strong>.
          </span>
        </div>
      </section>
      <section className={classes.profileSection}>
        <h3>Address</h3>
        <dl className={classes.readOnlyList}>
          <dt>Name</dt>
          <dd>{profile?.user?.name || "-"}</dd>
          <dt>Address</dt>
          <dd>{profile?.user?.address || "-"}</dd>
          <dt>City</dt>
          <dd>{profile?.user?.city || "-"}</dd>
          <dt>Zipcode</dt>
          <dd>{profile?.user?.zipcode || "-"}</dd>
        </dl>
      </section>
      <section className={classes.profileSection}>
        <h3>Password</h3>
        <TextField
          label="Current password"
          type="password"
          fullWidth
          margin="dense"
          variant="outlined"
          value={passwordForm.currentPassword}
          onChange={(e) =>
            setPasswordForm({
              ...passwordForm,
              currentPassword: e.target.value,
            })
          }
        />
        <TextField
          label="New password"
          type="password"
          fullWidth
          margin="dense"
          variant="outlined"
          value={passwordForm.newPassword}
          onChange={(e) =>
            setPasswordForm({ ...passwordForm, newPassword: e.target.value })
          }
        />
        <TextField
          label="Confirm new password"
          type="password"
          fullWidth
          margin="dense"
          variant="outlined"
          value={passwordForm.confirmPassword}
          onChange={(e) =>
            setPasswordForm({
              ...passwordForm,
              confirmPassword: e.target.value,
            })
          }
        />
        <Button
          color="primary"
          className={classes.actionButton}
          onClick={changePassword}
        >
          Change password
        </Button>
      </section>
    </div>
  );

  const apiPanel = () => (
    <div className={classes.profileGrid}>
      <section className={classes.profileSection}>
        <h3>API</h3>
        <dl className={classes.readOnlyList}>
          <dt>Email</dt>
          <dd>{profile?.email?.isVerified ? "Verified" : "Not verified"}</dd>
          <dt>Phone</dt>
          <dd>{profile?.email?.phone ? "On file" : "Required"}</dd>
        </dl>
        <Button
          color="primary"
          className={classes.actionButton}
          disabled={!apiAccessReady || apiRequestLoading}
          onClick={requestApiKey}
        >
          {apiRequestLoading ? "Sending..." : "Request API key"}
        </Button>
        {!apiAccessReady && (
          <div className={classes.verifyText}>
            Verify email and save a phone number in Profile before requesting API access.
          </div>
        )}
      </section>
      <section className={classes.profileSection}>
        <h3>API Document</h3>
        <a
          className={classes.apiDocLink}
          href={apiDocsUrl}
          target="_blank"
          rel="noreferrer"
        >
          Open API document
        </a>
      </section>
    </div>
  );

  const content =
    activeView === "map"
      ? ordersMap()
      : activeView === "files"
      ? filesOverview()
      : activeView === "profile"
      ? profilePanel()
      : activeView === "api"
      ? apiPanel()
      : ordersTable();

  return (
    <div
      className={`${classes.page} ${
        isSidebarCollapsed ? classes.pageCollapsed : ""
      }`}
    >
      <aside
        className={`${classes.sidebar} ${
          isSidebarCollapsed ? classes.sidebarCollapsed : ""
        }`}
      >
        <div className={classes.brandWrap}>
          <img src="/logos/fdl.png" alt="FDL DNT" className={classes.logo} />
          <button
            type="button"
            className={classes.sidebarToggle}
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            aria-label={isSidebarCollapsed ? "Expand menu" : "Collapse menu"}
          >
            <MenuIcon />
          </button>
        </div>
        <nav className={classes.navList}>
          <button
            type="button"
            className={activeView === "table" ? classes.navActive : ""}
            onClick={() => setActiveView("table")}
          >
            <TableChartIcon />
            Table
          </button>
          <button
            type="button"
            className={activeView === "files" ? classes.navActive : ""}
            onClick={() => setActiveView("files")}
          >
            <InsertDriveFileIcon />
            Files
          </button>
          <button
            type="button"
            disabled={!orders.length}
            className={activeView === "map" ? classes.navActive : ""}
            onClick={() => setActiveView("map")}
          >
            <MapIcon />
            Map
          </button>
          <button
            type="button"
            className={activeView === "profile" ? classes.navActive : ""}
            onClick={() => setActiveView("profile")}
          >
            <AccountCircleIcon />
            Profile
          </button>
          <button
            type="button"
            className={activeView === "api" ? classes.navActive : ""}
            onClick={() => setActiveView("api")}
          >
            <VpnKeyIcon />
            API
          </button>
        </nav>
        <button type="button" className={classes.logoutButton} onClick={logout}>
          <ExitToAppIcon />
          Logout
        </button>
      </aside>
      <main className={classes.mainPanel}>
        <header className={classes.topBar}>
          <div>
            <h1>Welcome {profile?.user?.name || ""}</h1>
            <p>
              {mode === "invoice"
                ? `Exact invoice search across ${windowLabel || "last 30 days"}`
                : selectedShipDate
                ? `Showing ship date ${selectedShipDate}`
                : "No data in the last 30 days"}
            </p>
          </div>
          {isLoadingOrder && <CircularProgress size={26} />}
        </header>
        {unverifiedEmail && (
          <div className={classes.emailPrompt}>
            <Tooltip title="Close">
              <button
                type="button"
                className={classes.emailPromptClose}
                onClick={() => setIsEmailPromptDismissed(true)}
                aria-label="Close verify email reminder"
              >
                <CloseIcon />
              </button>
            </Tooltip>
            <strong>Verify email</strong>
            <span>
              Set up a verified email in Profile for account recovery.
            </span>
            <button
              type="button"
              className={classes.emailPromptAction}
              onClick={() => setActiveView("profile")}
            >
              Open profile
            </button>
          </div>
        )}
        {notice && <div className={classes.notice}>{notice}</div>}
        <section className={classes.summaryGrid}>
          {summary.map((item) => (
            <div key={item.label} className={classes.summaryCard}>
              <span>{item.label}</span>
              <strong>{item.value}</strong>
            </div>
          ))}
        </section>
        <section className={classes.filterBar}>
          <TextField
            label="Ship Date"
            type="date"
            variant="outlined"
            size="small"
            value={selectedShipDate}
            onChange={(e) =>
              setShipDate(e.target.value ? moment(e.target.value) : null)
            }
            InputLabelProps={{ shrink: true }}
          />
          <Button
            color="primary"
            className={classes.iconButton}
            onClick={filterByDate}
          >
            Filter
          </Button>
          <TextField
            label="Invoice No."
            variant="outlined"
            size="small"
            value={invoice || ""}
            onChange={(e) => setInvoice(e.target.value)}
          />
          <Button
            color="primary"
            className={classes.iconButton}
            onClick={searchInvoice}
          >
            <SearchIcon />
            Search
          </Button>
          <Button
            className={classes.downloadButton}
            disabled={!orders.length}
            onClick={exportExcel}
          >
            <GetAppIcon />
            Excel
          </Button>
        </section>
        <section className={classes.contentPanel}>{content}</section>
        <Dialog
          open={trackingDialog.open}
          onClose={closeTrackingDialog}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle disableTypography className={classes.dialogTitle}>
            <div>
              <h2>Invoice {trackingDialog.row?.invoice}</h2>
              <p>
                {trackingDialog.row?.shipdate} - Route{" "}
                {trackingDialog.row?.routeId}
              </p>
            </div>
            <button
              type="button"
              className={classes.dialogClose}
              onClick={closeTrackingDialog}
              aria-label="Close delivery status"
            >
              <CloseIcon />
            </button>
          </DialogTitle>
          <DialogContent className={classes.dialogContent}>
            {trackingDialog.loading && <CircularProgress size={28} />}
            {trackingDialog.error && (
              <Alert severity="warning">{trackingDialog.error}</Alert>
            )}
            {!trackingDialog.loading && !trackingDialog.error && (
              <div className={classes.trackingList}>
                {trackingDialog.items.map((item, index) => (
                  <div
                    key={`${item.title}-${index}`}
                    className={classes.trackingItem}
                  >
                    <span
                      className={`${classes.trackingDot} ${
                        item.done ? classes.trackingDotDone : ""
                      }`}
                    />
                    <div>
                      <strong>{item.title || "Status"}</strong>
                      <p>{item.message}</p>
                      {item.liquorStoreName && <h3>{item.liquorStoreName}</h3>}
                      {item.liquorStoreAddress && (
                        <p>{getTrackingAddress(item.liquorStoreAddress)}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}

OrdersPage.propTypes = {
  orders: PropTypes.arrayOf(PropTypes.object).isRequired,
  setOrders: PropTypes.func.isRequired,
  shipDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  setShipDate: PropTypes.func.isRequired,
  invoice: PropTypes.string,
  setInvoice: PropTypes.func.isRequired,
};

OrdersPage.defaultProps = {
  shipDate: null,
  invoice: "",
};
